import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import ProposalsList from './proposals/ProposalsList';
import NewProposalDialog from './proposals/NewProposalDialog';
import ProposalDetailSheet from './proposals/ProposalDetailSheet';
import CreateProjectDialog from './proposals/CreateProjectDialog';
import DescriptionTemplatesPanel from './proposals/DescriptionTemplatesPanel';

export default function ProposalsPage() {
  const [proposals, setProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);

  const [descTemplates, setDescTemplates] = useState([]);
  const [descDefaultId, setDescDefaultId] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [tplPanelOpen, setTplPanelOpen] = useState(false);
  const [cpOpen, setCpOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const clientMap = {};
  (Array.isArray(clients) ? clients : []).forEach(c => { clientMap[c.id] = c.name; });

  const [exportingReport, setExportingReport] = useState(false);
  const exportActivityReport = async () => {
    setExportingReport(true);
    try {
      const r = await api.get('/proposals/export-activity-report', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Proposal_Activity_Report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Error exporting proposal report'); }
    setExportingReport(false);
  };

  const fetchProposals = () => api.get('/proposals').then(r => setProposals(Array.isArray(r.data) ? r.data : [])).catch(() => setProposals([]));
  const fetchClients = () => api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => setClients([]));
  const fetchProperties = () => api.get('/properties').then(r => setProperties(Array.isArray(r.data) ? r.data : [])).catch(() => setProperties([]));
  const fetchTemplates = () => api.get('/proposal-description-templates')
    .then(r => { setDescTemplates(Array.isArray(r.data?.templates) ? r.data.templates : []); setDescDefaultId(r.data?.default_id || null); })
    .catch(() => { setDescTemplates([]); setDescDefaultId(null); });

  useEffect(() => {
    fetchProposals().finally(() => setLoading(false));
    fetchClients();
    fetchProperties();
    api.get('/proposal-task-library').then(r => setLibrary(Array.isArray(r.data) ? r.data : [])).catch(() => setLibrary([]));
    fetchTemplates();
  }, []);

  const openDetail = async (p) => {
    setDetailOpen(true);
    setSelected(p);
    try { const r = await api.get(`/proposals/${p.id}`); setSelected(r.data); } catch { /* keep summary */ }
  };

  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      openDetail({ id: openId });
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleProposalCreated = (proposal) => {
    setAddOpen(false);
    fetchProposals();
    fetchClients();
    fetchProperties();
    openDetail(proposal);
  };

  return (
    <>
      <ProposalsList
        proposals={proposals}
        clientMap={clientMap}
        loading={loading}
        onOpenDetail={openDetail}
        onNewProposal={() => setAddOpen(true)}
        onOpenTemplates={() => setTplPanelOpen(true)}
        onExportReport={exportActivityReport}
        exporting={exportingReport}
      />

      <NewProposalDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        clients={clients}
        properties={properties}
        library={library}
        descTemplates={descTemplates}
        descDefaultId={descDefaultId}
        clientMap={clientMap}
        onCreated={handleProposalCreated}
      />

      <DescriptionTemplatesPanel
        open={tplPanelOpen}
        onOpenChange={setTplPanelOpen}
        descTemplates={descTemplates}
        fetchTemplates={fetchTemplates}
      />

      <ProposalDetailSheet
        open={detailOpen}
        onOpenChange={(o) => { if (!o) { setDetailOpen(false); setSelected(null); } }}
        selected={selected}
        setSelected={setSelected}
        clients={clients}
        properties={properties}
        clientMap={clientMap}
        onChanged={fetchProposals}
        onRequestCreateProject={() => setCpOpen(true)}
      />

      <CreateProjectDialog
        open={cpOpen}
        onOpenChange={setCpOpen}
        selected={selected}
        properties={properties}
      />
    </>
  );
}
